import { useQuery, useMutation, useQueryClient } from '../lib/queryClient';
import { toast } from 'sonner';
import { getTasksList } from '../endpoints/tasks/list_GET.schema';
import { createTask } from '../endpoints/tasks/create_POST.schema';
import { updateTask } from '../endpoints/tasks/update_POST.schema';
import { deleteTask } from '../endpoints/tasks/delete_POST.schema';

export const useTasks = (sessionId: string | undefined) => {
  return useQuery({
    queryKey: ['tasks', 'list', sessionId],
    queryFn: () => {
      if (!sessionId) throw new Error('Session ID is required');
      return getTasksList({ sessionId });
    },
    enabled: !!sessionId,
  });
};

export const useCreateTask = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createTask,
    onSuccess: (data) => {
      toast.success('Task created successfully');
      queryClient.invalidateQueries({ queryKey: ['tasks', 'list', data.task.sessionId] });
    },
    onError: (error) => {
      toast.error(`Failed to create task: ${error.message}`);
    },
  });
};

export const useUpdateTask = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateTask,
    onSuccess: (data) => {
      toast.success('Task updated');
      queryClient.invalidateQueries({ queryKey: ['tasks', 'list', data.task.sessionId] });
    },
    onError: (error) => {
      toast.error(`Failed to update task: ${error.message}`);
    },
  });
};

export const useDeleteTask = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteTask,
    onSuccess: (_, variables) => {
      toast.success('Task deleted');
      // We need to invalidate the list. Since we don't have sessionId in the output of delete,
      // we rely on the fact that the list query key includes sessionId.
      // Ideally, we should pass sessionId in the mutation context or invalidate all task lists.
      // For simplicity and safety, we invalidate all task lists.
      queryClient.invalidateQueries({ queryKey: ['tasks', 'list'] });
    },
    onError: (error) => {
      toast.error(`Failed to delete task: ${error.message}`);
    },
  });
};
